/**
 * buildEstadoBullets.ts — Generates personalised status bullets for P9_Reveal.
 *
 * V17 (Santiago 2026-04-28): static post-calculation bullets that replace the
 * old "Calibrando…/Adaptando…/Eligiendo…" gerunds. Each bullet surfaces a
 * concrete decision the calculator made for this specific dog, and is editable
 * from Admin → Contenido Funnel → Textos (keys under `p9.estado.*`).
 *
 * Priority order (highest → lowest):
 *  1. Breed size (gigante/grande → calcium + joint; pequeño/toy → small format;
 *     medio → balanced)
 *  2. Age (cachorro → caloric density; senior → lean protein + antioxidants;
 *     adulto → no bullet, most common, avoids noise)
 *  3. Body condition (sobrepeso/delgado → ration adjustment)
 *  4. Pathologies (articular/piel → omega-3; digestivo → soft recipes;
 *     obesidad → low-fat + fibre; other → generic adaptation)
 *  5. Allergies (list of excluded proteins)
 *  6. Activity level (alto → quality calories; bajo → adjusted ration;
 *     medio → no bullet, default)
 */

import { ActivityLevel, AgeCategory } from '../../../types';

export interface EstadoBullet {
  key: string;
  text: string;
}

type Translator = (key: string, fallback: string, params?: Record<string, string | number>) => string;

export function buildEstadoBullets(params: {
  nombre: string;
  breedDisplayName?: string;
  breedSize?: string;
  ageCategory?: AgeCategory;
  bodyCondition?: string;
  activityLevel?: ActivityLevel;
  allergies: string[];
  pathologies: string[];
  t: Translator;
}): EstadoBullet[] {
  const { nombre, breedDisplayName, breedSize, ageCategory, bodyCondition, activityLevel, allergies, pathologies, t } =
    params;
  const bullets: EstadoBullet[] = [];

  // 1. Tamaño raza
  if (breedSize === 'gigante' || breedSize === 'grande') {
    bullets.push({
      key: 'estado.tamano.grande',
      text: t('p9.estado.tamano.grande', 'Recetas con calcio extra y omega-3 para huesos y articulaciones grandes.'),
    });
  } else if (breedSize === 'pequeno' || breedSize === 'toy') {
    bullets.push({
      key: 'estado.tamano.pequeno',
      text: t('p9.estado.tamano.pequeno', 'Recetas en formato pequeño y fáciles de digerir para razas pequeñas.'),
    });
  } else if (breedSize === 'medio') {
    bullets.push({
      key: 'estado.tamano.medio',
      text: t('p9.estado.tamano.medio', 'Recetas equilibradas en proteína y energía para razas de tamaño medio.'),
    });
  }

  // 2. Edad
  if (ageCategory === AgeCategory.CACHORRO) {
    bullets.push({
      key: 'estado.edad.cachorro',
      text: t('p9.estado.edad.cachorro', 'Más densidad calórica y proteína para acompañar el crecimiento.'),
    });
  } else if (ageCategory === AgeCategory.SENIOR) {
    bullets.push({
      key: 'estado.edad.senior',
      text: t('p9.estado.edad.senior', 'Proteína magra y antioxidantes para el bienestar senior.'),
    });
  }

  // 3. Condición corporal
  if (bodyCondition === 'sobrepeso') {
    bullets.push({
      key: 'estado.cuerpo.sobrepeso',
      text: t('p9.estado.cuerpo.sobrepeso', 'Menos kcal y más fibra para perder peso de forma saludable.'),
    });
  } else if (bodyCondition === 'delgado') {
    bullets.push({
      key: 'estado.cuerpo.delgado',
      text: t('p9.estado.cuerpo.delgado', 'Aporte calórico extra para coger peso de forma saludable.'),
    });
  }

  // 4. Patologías — ramificamos por tipo más común. La detección por
  //    keywords es laxa porque las pathologies vienen del enum y de
  //    config dinámica.
  const pathSlugs = pathologies.filter((p) => p && p !== 'ninguna').map((p) => p.toLowerCase());
  if (pathSlugs.length > 0) {
    const hasArticular = pathSlugs.some((p) => /articul|displa|artros/.test(p));
    const hasPiel = pathSlugs.some((p) => /piel|pelo|dermat|atop/.test(p));
    const hasDigestivo = pathSlugs.some((p) => /digest|intest|gastr|colon/.test(p));
    const hasObesidad = pathSlugs.some((p) => /obesid|sobrepes/.test(p));

    if (hasArticular || hasPiel) {
      bullets.push({
        key: 'estado.salud.omega',
        text: t('p9.estado.salud.omega', 'Antioxidantes y omega-3 para piel, pelo y articulaciones.'),
      });
    } else if (hasDigestivo) {
      bullets.push({
        key: 'estado.salud.digestivo',
        text: t('p9.estado.salud.digestivo', 'Recetas suaves y proteína de fácil digestión para sistema sensible.'),
      });
    } else if (hasObesidad) {
      bullets.push({
        key: 'estado.salud.obesidad',
        text: t('p9.estado.salud.obesidad', 'Bajas en grasa y altas en fibra para controlar el peso.'),
      });
    } else {
      bullets.push({
        key: 'estado.salud.generico',
        text: t('p9.estado.salud.generico', 'Proteína y nutrientes adaptados a sus condiciones de salud.'),
      });
    }
  }

  // 5. Alergias
  const realAllergies = allergies.filter((a) => a && a !== 'ninguna');
  if (realAllergies.length) {
    const displayNames = realAllergies.slice(0, 3).map((a) => a.charAt(0).toUpperCase() + a.slice(1));
    bullets.push({
      key: 'estado.alergias',
      text: t('p9.estado.alergias', 'Evitamos {alergenos} en todas las recetas de {nombre}.', {
        alergenos: displayNames.join(', '),
        nombre,
      }),
    });
  }

  // 6. Actividad
  if (activityLevel === ActivityLevel.ALTO) {
    bullets.push({
      key: 'estado.actividad.alto',
      text: t('p9.estado.actividad.alto', 'Más calorías de calidad para mantener la energía en perros muy activos.'),
    });
  } else if (activityLevel === ActivityLevel.BAJO) {
    bullets.push({
      key: 'estado.actividad.bajo',
      text: t('p9.estado.actividad.bajo', 'Ración ajustada para que el peso no se descontrole con la baja actividad.'),
    });
  }

  // Si no se generó NINGÚN bullet específico (perfil "neutro": adulto,
  // medio, ideal, sin alergias ni patologías), añadimos uno por defecto
  // con el nombre/raza para que la columna no quede vacía.
  if (bullets.length === 0) {
    bullets.push({
      key: 'estado.fallback',
      text: breedDisplayName
        ? t('p9.estado.fallback.raza', 'Plan adaptado al perfil de {raza}.', { raza: breedDisplayName })
        : t('p9.estado.fallback.generico', 'Plan adaptado al perfil de {nombre}.', { nombre }),
    });
  }

  return bullets;
}
